import { useState, useEffect, useCallback } from 'react';
import { transformationRulesService, TransformationRule, CreateTransformationRuleData, UpdateTransformationRuleData } from '@/services/transformationRules.service';

export const useTransformationRules = () => {
  const [rules, setRules] = useState<TransformationRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Transform API data to match frontend expectations
  const transformRuleData = (rule: any): TransformationRule => {
    return {
      ...rule,
      // Add computed properties for backward compatibility
      sourceUnit: rule.sourceIngredient?.unitSymbol || rule.sourceIngredient?.unitName || 'kg',
      targetUnit: rule.targetIngredient?.unitSymbol || rule.targetIngredient?.unitName || 'kg',
    };
  };

  const loadRules = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Make real API call to get transformation rules
      const response = await transformationRulesService.getTransformationRules({
        includeInactive: true,
        sortBy: 'name',
        sortOrder: 'asc'
      });

      if (response.success && response.data) {
        // Transform the data to match frontend expectations
        const transformedRules = response.data.map(transformRuleData);
        setRules(transformedRules);
      } else {
        throw new Error(response.error || 'Error al cargar las reglas');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar las reglas de transformación');
      console.error('Error loading transformation rules:', err);
      // Set empty array on error instead of leaving old data
      setRules([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const createRule = async (ruleData: CreateTransformationRuleData) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await transformationRulesService.createTransformationRule(ruleData);

      if (response.success && response.data) {
        // Reload all rules to get updated data
        await loadRules();
        return response.data;
      } else {
        throw new Error(response.error || 'Error al crear la regla');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear la regla de transformación');
      console.error('Error creating transformation rule:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateRule = async (ruleId: string, ruleData: UpdateTransformationRuleData) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await transformationRulesService.updateTransformationRule(ruleId, ruleData);

      if (response.success && response.data) {
        // Reload all rules to get updated data
        await loadRules();
        return response.data;
      } else {
        throw new Error(response.error || 'Error al actualizar la regla');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar la regla de transformación');
      console.error('Error updating transformation rule:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRule = async (ruleId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await transformationRulesService.deleteTransformationRule(ruleId);

      if (response.success) {
        // Reload all rules to get updated data
        await loadRules();
        return true;
      } else {
        throw new Error(response.error || 'Error al eliminar la regla');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar la regla de transformación');
      console.error('Error deleting transformation rule:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getRulesBySourceIngredient = (sourceIngredientId: string) => {
    return rules.filter(rule => rule.sourceIngredientId === sourceIngredientId);
  };

  const getRulesByTargetIngredient = (targetIngredientId: string) => {
    return rules.filter(rule => rule.targetIngredientId === targetIngredientId);
  };

  return {
    rules,
    isLoading,
    error,
    createRule,
    updateRule,
    deleteRule,
    getRulesBySourceIngredient,
    getRulesByTargetIngredient,
    reload: loadRules
  };
};